import { useEffect, useMemo, useState } from "react";
import { api, ApiError, type HistoricoEquipamento as Historico, type ItemHistoricoEquipamento } from "../api";
import { TIPOS_ANALISE } from "../config";
import { SECOES_LAUDO } from "../laudo-campos";
import { formatDate } from "../format";
import { IconHistory, IconChevronDown } from "../icons";

interface Props {
  analiseId: number;
  /** O ensaio da tela: a tabela abre nele (dá para trocar ali mesmo). */
  secao: string;
  /** Muda quando o vínculo com o equipamento muda — recarrega o histórico. */
  recarregarQuando?: unknown;
}

/** Quantas análises anteriores cabem lado a lado antes de rolar para o lado. */
const MAX_ANTERIORES = 8;

/** Texto do laudo → número ("0,03", "< 0,5"); null quando não é número. */
function numero(v: string | undefined): number | null {
  const t = String(v ?? "").replace(/[<>≤≥]/g, "").replace(/\s/g, "").replace(",", ".");
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** O veredito do parecer no tom da casa (Normal / Atenção / Crítico). */
function tomDoStatus(s: string | null | undefined): string {
  const v = String(s ?? "").toLowerCase();
  if (v.startsWith("crít") || v.startsWith("crit")) return "danger";
  if (v.startsWith("aten")) return "warn";
  if (v.startsWith("normal")) return "ok";
  return "neutral";
}

/** Tem algum valor deste ensaio? */
const temValores = (i: ItemHistoricoEquipamento, ensaio: string) =>
  Object.keys(i.valores?.[ensaio] ?? {}).length > 0;

/**
 * O HISTÓRICO DO EQUIPAMENTO, dentro da etapa de diagnóstico.
 *
 * Fica NA MESMA JANELA do parecer, e não numa aba à parte: quem diagnostica
 * precisa olhar o passado e escrever o presente ao mesmo tempo — sair da
 * análise para consultar e voltar é o caminho em que se perde o que estava
 * digitado.
 *
 * O formato é o do laudo de papel: os parâmetros nas linhas e as análises nas
 * colunas, da mais recente para a mais antiga. É assim que a evolução se lê
 * (a água subindo de coleta em coleta salta aos olhos numa linha), sem
 * precisar de gráfico. A seta na coluna "Esta amostra" compara com a análise
 * anterior mais recente que mediu aquele parâmetro.
 */
export default function HistoricoEquipamento({ analiseId, secao, recarregarQuando }: Props) {
  const [dados, setDados] = useState<Historico | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(true);
  const [ensaio, setEnsaio] = useState(secao);

  useEffect(() => setEnsaio(secao), [secao]);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);
    api.analises
      .historicoEquipamento(analiseId)
      .then((d) => vivo && setDados(d))
      .catch((e) => vivo && setErro(e instanceof ApiError ? e.message : "Não foi possível carregar o histórico."))
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [analiseId, recarregarQuando]);

  const anteriores = dados?.anteriores ?? [];

  /** Os ensaios que têm o que mostrar (o da tela sempre aparece). */
  const ensaiosComDados = useMemo(() => {
    if (!dados) return [];
    return TIPOS_ANALISE.filter(
      (t) => t.key === secao || temValores(dados.atual, t.key) || anteriores.some((a) => temValores(a, t.key)),
    );
  }, [dados, anteriores, secao]);

  const colunas = useMemo(
    () => (dados ? [dados.atual, ...anteriores.slice(0, MAX_ANTERIORES)] : []),
    [dados, anteriores],
  );

  /** Os parâmetros do ensaio escolhido que têm valor em alguma coluna. */
  const linhas = useMemo(() => {
    const campos = SECOES_LAUDO.find((s) => s.selecao === ensaio)?.campos ?? [];
    const conhecidos = new Set(campos.map((c) => c.chave));
    const extras = new Set<string>();
    for (const c of colunas) for (const k of Object.keys(c.valores?.[ensaio] ?? {})) if (!conhecidos.has(k)) extras.add(k);
    return [...campos, ...[...extras].map((k) => ({ chave: k, label: k }))].filter((c) =>
      colunas.some((col) => String(col.valores?.[ensaio]?.[c.chave] ?? "").trim() !== ""),
    );
  }, [ensaio, colunas]);

  const equip = dados?.equipamento;
  const descEquip = equip
    ? [equip.tag, equip.numero_serie ? `série ${equip.numero_serie}` : null, equip.fabricante]
        .filter(Boolean)
        .join(" · ")
    : "achado pelo nº de série";

  if (carregando && !dados) {
    return <div className="heq heq-vazio">Carregando o histórico do equipamento...</div>;
  }
  if (erro) {
    return <div className="heq heq-vazio">Histórico do equipamento indisponível: {erro}</div>;
  }
  if (!dados) return null;

  if (!dados.criterio) {
    return (
      <div className="heq heq-vazio">
        <IconHistory size={15} /> Esta amostra não está ligada a um equipamento do cadastro (nem tem nº de
        série) — sem isso não há como achar as análises anteriores dele.
      </div>
    );
  }

  if (anteriores.length === 0) {
    return (
      <div className="heq heq-vazio">
        <IconHistory size={15} /> <strong>Histórico do equipamento</strong> ({descEquip}): esta é a primeira
        análise dele no sistema — não há resultado anterior para comparar.
      </div>
    );
  }

  const ultima = anteriores[0];

  return (
    <section className="heq">
      <button className="heq-topo" onClick={() => setAberto((v) => !v)} aria-expanded={aberto}>
        <IconHistory size={16} />
        <strong>Histórico do equipamento</strong>
        <span className="heq-sub">
          {descEquip} · {anteriores.length} análise(s) anterior(es)
          {ultima?.data ? ` · última em ${formatDate(ultima.data)}` : ""}
        </span>
        <span className={`nav-chevron ${aberto ? "aberto" : ""}`}>
          <IconChevronDown size={15} />
        </span>
      </button>

      {aberto && (
        <div className="heq-corpo">
          {ensaiosComDados.length > 1 && (
            <div className="heq-ensaios" role="tablist">
              {ensaiosComDados.map((t) => (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={ensaio === t.key}
                  className={`heq-ensaio ${ensaio === t.key ? "ativo" : ""}`}
                  onClick={() => setEnsaio(t.key)}
                >
                  <span className={`an-selo tom-${t.tom}`}>{t.sigla}</span> {t.titulo}
                </button>
              ))}
            </div>
          )}

          {linhas.length === 0 ? (
            <p className="heq-nota">
              Nenhuma análise deste equipamento tem resultado de{" "}
              {TIPOS_ANALISE.find((t) => t.key === ensaio)?.titulo ?? ensaio}.
            </p>
          ) : (
            <div className="heq-tabela-wrap">
              <table className="heq-tabela">
                <thead>
                  <tr>
                    <th>Parâmetro</th>
                    {colunas.map((c, i) => (
                      <th key={c.id} className={i === 0 ? "heq-atual" : ""}>
                        <span>{i === 0 ? "Esta amostra" : formatDate(c.data)}</span>
                        <em>{i === 0 ? (c.data ? formatDate(c.data) : "—") : `OS ${c.os_numero}`}</em>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {colunas.some((c) => c.temperatura_coleta) && (
                    <tr className="heq-linha-contexto">
                      <td>Temperatura na coleta (°C)</td>
                      {colunas.map((c, i) => (
                        <td key={c.id} className={i === 0 ? "heq-atual" : ""}>
                          {c.temperatura_coleta || "—"}
                        </td>
                      ))}
                    </tr>
                  )}
                  {linhas.map((campo) => {
                    const valores = colunas.map((c) => String(c.valores?.[ensaio]?.[campo.chave] ?? "").trim());
                    const atual = numero(valores[0]);
                    const iAnterior = valores.findIndex((v, i) => i > 0 && numero(v) !== null);
                    const anterior = iAnterior > 0 ? numero(valores[iAnterior]) : null;
                    let seta = "";
                    let titulo = "";
                    if (atual !== null && anterior !== null) {
                      seta = atual > anterior ? "▲" : atual < anterior ? "▼" : "=";
                      titulo = `${valores[iAnterior]} em ${formatDate(colunas[iAnterior]!.data)} → ${valores[0]} agora`;
                    }
                    return (
                      <tr key={campo.chave}>
                        <td>{campo.label}</td>
                        {valores.map((v, i) => (
                          <td key={colunas[i]!.id} className={i === 0 ? "heq-atual" : ""}>
                            {v || "—"}
                            {i === 0 && seta && (
                              <span className="heq-seta" title={titulo}>
                                {seta}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  <tr className="heq-linha-parecer">
                    <td>Parecer</td>
                    {colunas.map((c, i) => {
                      const p = c.pareceres?.[ensaio];
                      return (
                        <td key={c.id} className={i === 0 ? "heq-atual" : ""}>
                          {i === 0 ? (
                            <span className="heq-nota-cel">em diagnóstico</span>
                          ) : p?.status ? (
                            <span className={`pill pill-${tomDoStatus(p.status)}`} title={p.descricao ?? ""}>
                              {p.status}
                            </span>
                          ) : (
                            "—"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <p className="heq-nota">
            {dados.criterio === "cadastro"
              ? "Análises do mesmo equipamento do cadastro, de qualquer OS."
              : "Esta amostra não está ligada ao cadastro: as análises foram achadas pelo nº de série do mesmo cliente."}
            {anteriores.length > MAX_ANTERIORES
              ? ` Mostrando as ${MAX_ANTERIORES} mais recentes de ${anteriores.length}.`
              : ""}{" "}
            Passe o mouse no parecer para ler o texto e na seta para ver a comparação.
          </p>
        </div>
      )}
    </section>
  );
}
